import React, { useState, useEffect, useContext } from 'react'
import { Table, Button, Input } from 'semantic-ui-react'

import { deletePriceList, PriceList } from '../../services/priceList'
import { PaginationContext, Pagination } from '../../contexts/PaginationContext'
import { useCan } from '../../hooks/useCan'
import { can } from '../../config/permissions'
import { PaginationControllers } from '../../components/PaginationControllers'
import { ConfirmationModal } from '../../components/ConfirmationModal'
import { TableHeaderCell, ItemControllers } from '../../components/Table'
import { DefaultLoader } from '../../components/DefaultLoader'
import { ActionsContainer } from '../../components/RegistrationContainer/styles'

interface PriceListsTableProps {
  priceLists: PriceList[]
  totalItems: number
  loading?: boolean
  fetchPriceLists: (pagination: Pagination) => void
  onCreateClick?: () => void
  onUpdateClick?: (priceList: PriceList) => void
}

export const PriceListsTable: React.FC<PriceListsTableProps> = ({
  priceLists,
  totalItems,
  loading,
  fetchPriceLists,
  onCreateClick,
  onUpdateClick
}) => {
  const {
    pagination,
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,
    changeOrderBy,
    changeLimit,
    changeQuery
  } = useContext(PaginationContext)
  const [search, setSearch] = useState('')
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [selectedPriceList, setSelectedPriceList] = useState<PriceList>()
  const canCreate = useCan(can.service.create)

  useEffect(() => {
    fetchPriceLists(pagination)
  }, [pagination])

  function searchPriceList() {
    changeQuery({ search })
  }

  function handleSearchKeyPress(evt: React.KeyboardEvent) {
    if (evt.key === 'Enter') {
      searchPriceList()
    }
  }

  function handleUpdateClick(priceList: PriceList) {
    onUpdateClick?.(priceList)
  }

  function handleDeleteClick(priceList: PriceList) {
    setDeleteModal(true)
    setSelectedPriceList(priceList)
  }

  function handleDeletePriceList() {
    if (!selectedPriceList) return

    Promise.resolve()
      .then(() => setDeleteLoading(true))
      .then(() => deletePriceList(selectedPriceList.id))
      .then(() => setDeleteModal(false))
      .then(() => fetchPriceLists(pagination))
      .finally(() => setDeleteLoading(false))
  }

  return (
    <>
      <DefaultLoader active={loading} />
      <ActionsContainer>
        <Input
          placeholder="Pesquisar"
          value={search}
          onChange={(_, { value }) => setSearch(value.toUpperCase())}
          onKeyPress={handleSearchKeyPress}
          action={<Button icon="search" onClick={searchPriceList} />}
        />
        {onCreateClick && canCreate && (
          <Button
            content="Incluir"
            onClick={onCreateClick}
            floated="right"
            primary
          />
        )}
      </ActionsContainer>
      <Table selectable striped compact sortable>
        <Table.Header>
          <Table.Row>
            <TableHeaderCell
              content="Nome"
              name="name"
              pagination={pagination}
              onClick={() => changeOrderBy('name')}
            />
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {priceLists.map(priceList => (
            <Table.Row key={priceList.id}>
              <Table.Cell content={priceList.name} />
              <Table.Cell textAlign="right">
                <ItemControllers
                  onUpdateClick={() => handleUpdateClick(priceList)}
                  onDeleteClick={() => handleDeleteClick(priceList)}
                  permissionsToUpdate={can.priceList.update}
                  permissionsToDelete={can.priceList.delete}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={2} textAlign="right">
              <PaginationControllers
                count={priceLists.length}
                page={pagination.page}
                limit={pagination.limit}
                total={totalItems}
                loading={loading}
                goToFirstPage={goToFirstPage}
                goToPreviousPage={goToPreviousPage}
                goToNextPage={goToNextPage}
                goToLastPage={goToLastPage}
                onChangeLimit={changeLimit}
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
      <ConfirmationModal
        open={deleteModal}
        loading={deleteLoading}
        handleModal={setDeleteModal}
        onConfirm={handleDeletePriceList}
        onDecline={() => setDeleteModal(false)}
      >
        <span>Deseja excluir a tabela de venda?</span>
        <br />
        <strong>{selectedPriceList?.name}</strong>
      </ConfirmationModal>
    </>
  )
}
